import React from 'react';
import {
  Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ComposedChart, Line,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber } from '../types';

interface RevenueTabProps {
  period: PeriodConfig;
}

const RevenueTab: React.FC<RevenueTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(['revenue-dashboard'], period);

  if (loading) return <LoadingSpinner />;

  const rev = data['revenue-dashboard'];
  const rd = rev || {};
  const dailyRev = rd.dailyRevenue || [];
  const todayProducts = rd.todayProducts || [];
  const todayCountries = rd.todayByCountry || [];
  const trialToday = rd.trialToday || [];
  const variantFunnel = rd.variantFunnel || [];
  const sourceFunnel = rd.sourceFunnel || [];
  const recentOrders = rd.recentOrders || [];
  const checkoutTiming = rd.checkoutTiming || [];
  const closeTimingData = rd.closeTiming || [];
  const quizStats = rd.quizStats || {};

  // Build variant funnel table
  const variants = ['trial', 'simple', 'coins'];
  const funnelEvents = [
    'trial_paywall_view',
    'trial_paywall_plan_select',
    'trial_paywall_checkout_start',
    'trial_paywall_checkout_success',
    'trial_paywall_checkout_fail',
    'paywall_close_button_tap',
  ];
  const eventLabels: Record<string, string> = {
    'trial_paywall_view': 'Views',
    'trial_paywall_plan_select': 'Plan Select',
    'trial_paywall_checkout_start': 'Checkout',
    'trial_paywall_checkout_success': 'Success',
    'trial_paywall_checkout_fail': 'Fail',
    'paywall_close_button_tap': 'Close',
  };
  const vfMap: Record<string, Record<string, number>> = {};
  for (const v of variantFunnel) {
    if (!vfMap[v.variant]) vfMap[v.variant] = {};
    vfMap[v.variant][v.event] = v.uniqueUsers;
  }

  // Build source funnel table
  const sfMap: Record<string, Record<string, number>> = {};
  for (const s of sourceFunnel) {
    if (!sfMap[s.source]) sfMap[s.source] = {};
    sfMap[s.source][s.event] = s.uniqueUsers;
  }
  const sortedSources = Object.entries(sfMap)
    .map(([source, events]) => ({
      source,
      views: events['trial_paywall_view'] || 0,
      checkout: events['trial_paywall_checkout_start'] || 0,
      success: events['trial_paywall_checkout_success'] || 0,
      close: events['paywall_close_button_tap'] || 0,
    }))
    .filter(s => s.views > 0)
    .sort((a, b) => b.views - a.views);

  // Trial funnel map
  const trialMap: Record<string, any> = {};
  for (const t of trialToday) trialMap[t.type] = t;

  const localTooltipStyle = {
    background: '#1e293b',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: '#fff',
  };

  return (
    <div className="space-y-6">
      {/* -- Top Stats -- */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard title="Today Revenue" value={`$${(rd.todayRevenue || 0).toFixed(2)}`} subtitle={(() => { const today = (rd.dailyRevenue || []).slice(-1)[0] || {}; return `Subs $${(today.subs || 0).toFixed(0)} · Coins $${(today.coins || 0).toFixed(0)} · Tokens $${(today.diamonds || 0).toFixed(0)} · Streak $${(today.streak || 0).toFixed(0)}`; })()} />
        <StatCard title="This Month" value={`$${(rd.monthRevenue || 0).toFixed(2)}`} subtitle={`Subs $${(rd.monthSubs || 0).toFixed(0)} · Coins $${(rd.monthCoins || 0).toFixed(0)} · Tokens $${(rd.monthDiamonds || 0).toFixed(0)} · Streak $${(rd.monthStreak || 0).toFixed(0)}`} />
        <StatCard title="Registrations" value={rd.todayRegistrations || 0} subtitle="New users today" />
        <StatCard title="Trial Starts" value={trialMap['INITIAL_PURCHASE']?.count || 0} subtitle="Free trials started" />
        <StatCard title="Trial Converted" value={trialMap['RENEWAL']?.count || 0} subtitle={`$${(trialMap['RENEWAL']?.revenue || 0).toFixed(2)}`} />
        <StatCard title="Quiz Done" value={quizStats.completed || 0} subtitle={`${quizStats.skipped || 0} skipped`} />
      </div>

      {/* -- Daily Revenue + Trial Starts Chart -- */}
      <ChartCard title="Daily Revenue & Trial Starts (30 days)">
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={dailyRev}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
            <YAxis yAxisId="revenue" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: number) => `$${v}`} />
            <YAxis yAxisId="count" orientation="right" stroke="#64748b" fontSize={11} tickLine={false} />
            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
              if (!active || !payload?.length) return null;
              const d = payload[0]?.payload || {};
              return (
                <div style={{ ...localTooltipStyle, padding: '10px 14px', fontSize: '13px' }}>
                  <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                  <p style={{ color: '#4ade80' }}>Subscriptions: ${Number(d.subs || 0).toFixed(2)}</p>
                  <p style={{ color: '#FF9C15' }}>Coins: ${Number(d.coins || 0).toFixed(2)}</p>
                  {(d.diamonds || 0) > 0 && <p style={{ color: '#c084fc' }}>Tokens: ${Number(d.diamonds).toFixed(2)}</p>}
                  {(d.streak || 0) > 0 && <p style={{ color: '#38bdf8' }}>Streak: ${Number(d.streak).toFixed(2)}</p>}
                  <p style={{ color: '#fff', fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total: ${Number(d.total || 0).toFixed(2)}</p>
                  <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6 }}>
                    <p style={{ color: '#c084fc' }}>Trial Starts: {d.trialStarts || 0}</p>
                    <p style={{ color: '#38bdf8' }}>Trial &rarr; Paid: {d.trialRenewals || 0}</p>
                    <p style={{ color: '#f87171' }}>Cancellations: {d.allCancels || 0}</p>
                  </div>
                </div>
              );
            }} />
            <Legend />
            <Bar yAxisId="revenue" dataKey="subs" fill="#4ade80" name="Sub Revenue" stackId="a" />
            <Bar yAxisId="revenue" dataKey="coins" fill="#FF9C15" name="Coin Revenue" stackId="a" />
            <Bar yAxisId="revenue" dataKey="diamonds" fill="#c084fc" name="Token Revenue" stackId="a" />
            <Bar yAxisId="revenue" dataKey="streak" fill="#38bdf8" name="Streak Revenue" stackId="a" />
            <Line yAxisId="count" type="monotone" dataKey="trialStarts" stroke="#c084fc" strokeWidth={2} dot={false} name="Trial Starts" />
            <Line yAxisId="count" type="monotone" dataKey="trialRenewals" stroke="#38bdf8" strokeWidth={2} dot={false} name="Trial &rarr; Paid" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* -- Trial & Subscription Lifecycle (30d) -- */}
      <ChartCard title="Trial & Subscription Lifecycle (30 days)">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={dailyRev}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
            <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
            <Legend />
            <Bar dataKey="trialStarts" fill="rgba(192,132,252,0.3)" stroke="#c084fc" name="Trial Starts" />
            <Bar dataKey="allPurchases" fill="rgba(255,156,21,0.3)" stroke="#FF9C15" name="All Purchases" />
            <Line type="monotone" dataKey="trialRenewals" stroke="#4ade80" strokeWidth={2.5} dot={{ r: 3, fill: '#4ade80' }} name="Trial &rarr; Paid" />
            <Line type="monotone" dataKey="allRenewals" stroke="#38bdf8" strokeWidth={2} dot={false} name="All Renewals" />
            <Line type="monotone" dataKey="allCancels" stroke="#f87171" strokeWidth={2} dot={false} name="Cancellations" />
            <Line type="monotone" dataKey="billingIssues" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="Billing Issues" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* -- Variant Funnel -- */}
      <ChartCard title="Variant A/B/C Funnel (Today)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-gray-400 py-2 px-3">Metric</th>
                {variants.map(v => (
                  <th key={v} className="text-right text-gray-400 py-2 px-3 capitalize">
                    {v} ({v === 'trial' ? 'A' : v === 'simple' ? 'B' : 'C'})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {funnelEvents.map(e => (
                <tr key={e} className="border-b border-white/5">
                  <td className="text-gray-300 py-2 px-3">{eventLabels[e]}</td>
                  {variants.map(v => {
                    const val = vfMap[v]?.[e] || 0;
                    const views = vfMap[v]?.['trial_paywall_view'] || 0;
                    const pct = views > 0 ? ` (${((val / views) * 100).toFixed(0)}%)` : '';
                    const color = e.includes('success')
                      ? 'text-green-400'
                      : e.includes('fail')
                        ? 'text-red-400'
                        : e.includes('close')
                          ? 'text-yellow-400'
                          : 'text-white';
                    return (
                      <td key={v} className={`text-right py-2 px-3 font-mono ${color}`}>
                        {formatNumber(val)}
                        {e !== 'trial_paywall_view' ? <span className="text-gray-500 text-xs">{pct}</span> : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* -- Today by Product -- */}
        <ChartCard title="Today Revenue by Product">
          {todayProducts.length > 0 ? (
            <div className="space-y-3 p-2">
              {todayProducts.map((p: any, i: number) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">{p.product}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{p.users} users</span>
                    <span className="text-green-400 font-semibold">${p.revenue.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No revenue yet today</p>
          )}
        </ChartCard>

        {/* -- Today by Country -- */}
        <ChartCard title="Today Revenue by Country">
          {todayCountries.length > 0 ? (
            <div className="space-y-3 p-2">
              {todayCountries.map((c: any, i: number) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">{c.country}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{c.users} users</span>
                    <span className="text-green-400 font-semibold">${c.revenue.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No revenue yet today</p>
          )}
        </ChartCard>
      </div>

      {/* -- Source Performance -- */}
      <ChartCard title="Paywall Source Performance (Today)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-gray-400 py-2 px-3">Source</th>
                <th className="text-right text-gray-400 py-2 px-3">Views</th>
                <th className="text-right text-gray-400 py-2 px-3">Checkout</th>
                <th className="text-right text-gray-400 py-2 px-3">Conv%</th>
                <th className="text-right text-gray-400 py-2 px-3">Close%</th>
              </tr>
            </thead>
            <tbody>
              {sortedSources.map((s, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="text-gray-300 py-2 px-3">{s.source}</td>
                  <td className="text-right text-white py-2 px-3 font-mono">{s.views}</td>
                  <td className="text-right text-white py-2 px-3 font-mono">{s.checkout}</td>
                  <td className={`text-right py-2 px-3 font-mono ${s.views > 0 && (s.checkout / s.views) > 0.15 ? 'text-green-400' : 'text-red-400'}`}>
                    {s.views > 0 ? ((s.checkout / s.views) * 100).toFixed(0) : 0}%
                  </td>
                  <td className="text-right text-yellow-400 py-2 px-3 font-mono">
                    {s.views > 0 ? ((s.close / s.views) * 100).toFixed(0) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* -- Checkout Timing -- */}
        <ChartCard title="Time to Checkout (Today)">
          <div className="space-y-3 p-2">
            {checkoutTiming.map((t: any, i: number) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-gray-400 capitalize">{t.variant}</span>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500">{t.count} checkouts</span>
                  <span className="text-white font-mono">med {t.medianSec}s</span>
                  <span className="text-gray-500 font-mono">avg {t.avgSec}s</span>
                </div>
              </div>
            ))}
            {checkoutTiming.length === 0 && (
              <p className="text-gray-500 text-center py-4">No timing data yet</p>
            )}
          </div>
        </ChartCard>

        {/* -- Close Button Timing -- */}
        <ChartCard title="Time to Close (Today)">
          <div className="space-y-3 p-2">
            {closeTimingData.map((c: any, i: number) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-gray-400 capitalize">{c.variant}</span>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500">{c.count} closes</span>
                  <span className="text-white font-mono">med {c.medianSec}s</span>
                  <span className="text-gray-500 font-mono">avg {c.avgSec}s</span>
                </div>
              </div>
            ))}
            {closeTimingData.length === 0 && (
              <p className="text-gray-500 text-center py-4">No timing data yet</p>
            )}
          </div>
        </ChartCard>
      </div>

      {/* -- Trial Funnel Today -- */}
      <ChartCard title="Trial Funnel (Today)">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 p-2">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{trialMap['INITIAL_PURCHASE']?.count || 0}</p>
            <p className="text-xs text-gray-400">Starts</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-400">{trialMap['CANCELLATION']?.count || 0}</p>
            <p className="text-xs text-gray-400">Cancelled</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-400">{trialMap['BILLING_ISSUE']?.count || 0}</p>
            <p className="text-xs text-gray-400">Billing Issue</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-400">{trialMap['EXPIRATION']?.count || 0}</p>
            <p className="text-xs text-gray-400">Expired</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">{trialMap['RENEWAL']?.count || 0}</p>
            <p className="text-xs text-gray-400">Converted</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-400">{trialMap['PRODUCT_CHANGE']?.count || 0}</p>
            <p className="text-xs text-gray-400">Plan Change</p>
          </div>
        </div>
      </ChartCard>

      {/* -- Recent Orders (Live Feed) -- */}
      <ChartCard title="Recent Orders (Live)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-gray-400 py-2 px-3">Order Date</th>
                <th className="text-left text-gray-400 py-2 px-3">Cleared</th>
                <th className="text-left text-gray-400 py-2 px-3">Product</th>
                <th className="text-left text-gray-400 py-2 px-3">Type</th>
                <th className="text-left text-gray-400 py-2 px-3">Store</th>
                <th className="text-left text-gray-400 py-2 px-3">Country</th>
                <th className="text-left text-gray-400 py-2 px-3">User ID</th>
                <th className="text-left text-gray-400 py-2 px-3">Order ID</th>
                <th className="text-right text-gray-400 py-2 px-3">Price</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o: any, i: number) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                    {o.orderedAt ? new Date(o.orderedAt).toLocaleString() : '\u2014'}
                  </td>
                  <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                    {o.clearedAt ? new Date(o.clearedAt).toLocaleString() : '\u2014'}
                  </td>
                  <td className="text-white py-2 px-3">{o.product}</td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      o.type === 'RENEWAL'
                        ? 'bg-green-500/10 text-green-400'
                        : o.type === 'INITIAL_PURCHASE'
                          ? 'bg-blue-500/10 text-blue-400'
                          : 'bg-orange-500/10 text-orange-400'
                    }`}>
                      {o.type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      o.store === 'Android'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-blue-500/10 text-blue-400'
                    }`}>
                      {o.store || '?'}
                    </span>
                  </td>
                  <td className="text-gray-400 py-2 px-3">{o.country}</td>
                  <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                    {o.userId ? (
                      <button
                        className="hover:text-white transition-colors cursor-pointer text-left"
                        onClick={() => { navigator.clipboard.writeText(o.userId); }}
                        title="Click to copy"
                      >
                        {o.userId.startsWith('$RC') ? <span className="text-red-400">RC Anonymous</span> : o.userId}
                      </button>
                    ) : '\u2014'}
                  </td>
                  <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                    {o.orderId ? (
                      <button
                        className="hover:text-white transition-colors cursor-pointer text-left"
                        onClick={() => { navigator.clipboard.writeText(o.orderId); }}
                        title="Click to copy"
                      >
                        {o.orderId}
                      </button>
                    ) : '\u2014'}
                  </td>
                  <td className="text-right text-green-400 py-2 px-3 font-mono">
                    ${o.price?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
};

export default RevenueTab;
